import { computeInteractionSideEffects } from 'features/libra/lifecycle/lifecycleSideEffects'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useGeckoPermissions } from 'features/permissions'
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { deriveTicketPatch } from './deriveTicketPatch'
import {
  LibraTicket,
  TICKET_UPDATE_FIELD_KEYS,
  UpdateTicketArgs,
} from './libraTicketsApi'

/**
 * Function form of an enqueued write. Receives the latest committed ticket
 * snapshot (which the writer keeps in sync after each successful
 * `doUpdateTicket` call so write N sees write N-1's effects even when React /
 * SWR has not delivered the update back into the tree yet) and returns the
 * `UpdateTicketArgs` patch to issue. Return `null` to suppress the caller's
 * own patch — the writer may still emit a write if `applyInteractionSideEffects`
 * is left at its default `true` AND the merged side-effect patch (contributors
 * + FIRST_INTERACTION) is non-empty; otherwise the queue resolves the item
 * with no Rolodex call. Combine with `applyInteractionSideEffects: false` for
 * a guaranteed no-op (used by lifecycle-internal writes that shouldn't
 * retrigger FIRST_INTERACTION).
 */
export type TicketWriteFn = (
  latest: LibraTicket
) => Omit<UpdateTicketArgs, 'ticketId'> | null

export interface TicketWriterEnqueueOptions {
  /**
   * Controls whether contributor / `FIRST_INTERACTION` side effects merge
   * into this write. Defaults to `true` for substantive writes. Set to
   * `false` for lifecycle-internal writes (e.g. `completeWorkflow`, action
   * transitions) that ARE the lifecycle and shouldn't trigger
   * `FIRST_INTERACTION` re-entry.
   */
  applyInteractionSideEffects?: boolean
}

export interface TicketWriterContextType {
  /**
   * Append a write to the per-ticket FIFO queue. Resolves once the queue
   * has processed the item, with the patch YOUR write fn returned -- so
   * callers can act on what was actually applied (e.g. dispatch a
   * notification for the `ticketStatus` that landed) without smuggling
   * state out of the closure. Resolves `null` when the write fn skipped
   * (returned null) or the item was dropped before its closure ran
   * (unmount drain, enqueue-after-unmount). NOTE: the resolved value is
   * the caller's patch, NOT the side-effect-merged write -- with
   * interaction side effects enabled the emitted write may carry a
   * FIRST_INTERACTION `ticketStatus` the resolved patch deliberately does
   * not reflect. Hard errors (network, schema) reject.
   */
  enqueue: (
    write: TicketWriteFn,
    opts?: TicketWriterEnqueueOptions
  ) => Promise<Omit<UpdateTicketArgs, 'ticketId'> | null>
  /** True while the queue has work in flight. */
  isSaving: boolean
}

const TicketWriterContext = createContext<TicketWriterContextType | null>(null)

interface QueueItem {
  write: TicketWriteFn
  opts: TicketWriterEnqueueOptions
  resolve: (applied: Omit<UpdateTicketArgs, 'ticketId'> | null) => void
  reject: (err: unknown) => void
}

export const TicketWriterProvider: FC<{
  children: ReactNode
  ticket: LibraTicket
  doUpdateTicket: (args: UpdateTicketArgs) => Promise<void>
}> = ({ children, ticket, doUpdateTicket }) => {
  const workflow = useWorkflow()
  const { user } = useGeckoPermissions()

  /**
   * Mutable snapshot of the latest committed ticket. Updated synchronously
   * after every successful `doUpdateTicket` so the next queued write sees a
   * fresh base even when React / SWR has not delivered the update back into
   * props yet. Initialized from the prop and reconciled with prop updates
   * when the queue is idle.
   */
  const committedTicketRef = useRef<LibraTicket>(ticket)
  const queueRef = useRef<QueueItem[]>([])
  const isFlushingRef = useRef(false)
  const unmountedRef = useRef(false)
  const [isSaving, setIsSaving] = useState(false)

  // Reconcile the ref with the latest prop only when the queue is idle.
  // While writes are in flight, the ref leads SWR (write N's optimistic
  // result is in the ref before SWR delivers it).
  //
  // If the prop changes WHILE the queue is flushing, we can't accept it
  // synchronously (we'd clobber the in-progress optimistic state), but we
  // also can't drop it — otherwise a later write would commit against a
  // stale base if the prop was a true server update (e.g. another tab,
  // an SWR background refresh). `latestPropTicketRef` always tracks the
  // current prop; `propChangedDuringFlushRef` records the case where the
  // prop changed mid-flight. `flush()`'s finally block accepts the
  // queued-up prop once the queue drains.
  const latestPropTicketRef = useRef<LibraTicket>(ticket)
  const propChangedDuringFlushRef = useRef(false)
  useEffect(() => {
    latestPropTicketRef.current = ticket
    if (!isFlushingRef.current && queueRef.current.length === 0) {
      committedTicketRef.current = ticket
    } else {
      propChangedDuringFlushRef.current = true
    }
  }, [ticket])

  // Hard reset on ticket-id change. If the provider is reused across
  // different tickets (no `key={ticket.id}` at the consumer), the
  // reconcile effect above only swaps committedTicketRef when the queue
  // is idle — but a queued write for ticket A would then commit under
  // ticket B's id with A's optimistic data. Drop any pending items and
  // re-baseline committedTicketRef synchronously.
  const previousTicketIdRef = useRef(ticket.id)
  useEffect(() => {
    if (previousTicketIdRef.current === ticket.id) return
    previousTicketIdRef.current = ticket.id
    committedTicketRef.current = ticket
    const pending = queueRef.current
    queueRef.current = []
    const dropError = new Error(
      'TicketWriter dropped queued write after ticket switch: ticketId changed mid-flow'
    )
    for (const item of pending) {
      item.reject(dropError)
    }
    isFlushingRef.current = false
    setIsSaving(false)
  }, [ticket])

  useEffect(() => {
    // Reset on setup so React 18 strict-mode's setup→cleanup→setup replay
    // doesn't leave the ref stuck at `true` for the remainder of the session.
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
      // Resolve every pending write as a no-op so callers awaiting on
      // navigation away don't see an unhandled rejection.
      const pending = queueRef.current
      queueRef.current = []
      for (const item of pending) item.resolve(null)
    }
  }, [])

  const flush = useCallback(async () => {
    if (isFlushingRef.current) return
    isFlushingRef.current = true
    setIsSaving(true)
    try {
      while (queueRef.current.length > 0) {
        if (unmountedRef.current) break
        const item = queueRef.current.shift()
        if (!item) break
        try {
          const latest = committedTicketRef.current
          const patch = deriveTicketPatch(workflow, latest, item.write(latest))
          // Compute interaction side effects (contributors + FIRST_INTERACTION)
          // unless explicitly skipped.
          const sideEffects =
            item.opts.applyInteractionSideEffects === false
              ? {}
              : computeInteractionSideEffects({
                  lifecycleConfig: workflow.lifecycleConfig,
                  ticketStatus: latest.ticketStatus,
                  contributors: latest.contributors,
                  userId: user?.id,
                })

          const merged: Omit<UpdateTicketArgs, 'ticketId'> = {
            ...(patch ?? {}),
            // Side effects only override patch fields that weren't explicitly set.
            ...(sideEffects.contributors !== undefined &&
            patch?.contributors === undefined
              ? { contributors: sideEffects.contributors }
              : {}),
            ...(sideEffects.ticketStatus !== undefined &&
            patch?.ticketStatus === undefined
              ? { ticketStatus: sideEffects.ticketStatus }
              : {}),
          }

          if (Object.keys(merged).length === 0) {
            // Nothing to write (e.g. skipped patch + no side effects).
            // Resolve with the caller's patch (null or {}) -- no write ran.
            item.resolve(patch)
            continue
          }

          await doUpdateTicket({ ticketId: latest.id, ...merged })

          // Bail post-await if the ticket switched mid-write. The reset
          // effect above already re-baselined committedTicketRef to the
          // new ticket and rejected the sibling queue items it could see
          // — but this item was already shifted out of queueRef before
          // the reset ran, so the reset's reject loop did not cover it.
          // Letting the optimistic snapshot apply here would clobber the
          // new baseline with the old ticket's data and a subsequent
          // ticket-B write would commit against the stale A snapshot.
          // The page-level `key={ticket.id}` on LibraView has already
          // unmounted the consumer tree for ticket A, so resolving here is
          // purely defensive — no awaited UI remains. Resolve with the
          // caller's patch: the write DID land, so an awaiting caller that
          // acts on the result (e.g. completeWorkflow's notification) sees
          // the same outcome as the normal success path.
          if (previousTicketIdRef.current !== latest.id) {
            item.resolve(patch)
            break
          }

          // Optimistically apply the patch to the committed snapshot so the
          // next queued write sees write N's effects without waiting for SWR.
          committedTicketRef.current = applyPatchToTicket(latest, merged)
          item.resolve(patch)
        } catch (err) {
          // Poison the queue on first hard failure: reject the failed item,
          // then reject every remaining queued item with a queue-aborted
          // error. The committed snapshot does NOT include the failed
          // patch, so letting later writes commit against it would silently
          // rebuild state the failed write was supposed to introduce. The
          // next explicit user interaction re-enqueues from fresh ticket
          // state (the `useEffect` above reconciles `committedTicketRef`
          // with the prop once the queue is idle).
          item.reject(err)
          const remaining = queueRef.current
          queueRef.current = []
          const abortError = new Error(
            'TicketWriter queue aborted after a prior write failed'
          )
          for (const pending of remaining) {
            pending.reject(abortError)
          }
          // Exit the loop; `finally` resets isFlushingRef so the next
          // enqueue from a fresh user action can flush again.
          break
        }
      }
    } finally {
      isFlushingRef.current = false
      setIsSaving(false)
      // Drained — if a fresh prop arrived during the flush, accept it
      // now so the next write rebases from the latest server state
      // rather than the now-stale optimistic snapshot.
      if (queueRef.current.length === 0 && propChangedDuringFlushRef.current) {
        committedTicketRef.current = latestPropTicketRef.current
        propChangedDuringFlushRef.current = false
      }
    }
  }, [doUpdateTicket, workflow, user?.id])

  const enqueue = useCallback(
    (write: TicketWriteFn, opts: TicketWriterEnqueueOptions = {}) =>
      new Promise<Omit<UpdateTicketArgs, 'ticketId'> | null>(
        (resolve, reject) => {
          if (unmountedRef.current) {
            resolve(null)
            return
          }
          queueRef.current.push({ write, opts, resolve, reject })
          // Fire-and-forget; errors propagate via item.reject inside flush.
          void flush()
        }
      ),
    [flush]
  )

  const value = useMemo<TicketWriterContextType>(
    () => ({ enqueue, isSaving }),
    [enqueue, isSaving]
  )

  return (
    <TicketWriterContext.Provider value={value}>
      {children}
    </TicketWriterContext.Provider>
  )
}

export const useTicketWriter = (): TicketWriterContextType => {
  const ctx = useContext(TicketWriterContext)
  if (!ctx) {
    throw new Error(
      'useTicketWriter must be used within a TicketWriterProvider'
    )
  }
  return ctx
}

/**
 * Optimistically merge the patch into the committed snapshot. Mirrors what
 * `doUpdateTicket` will eventually do server-side. Conservative: only
 * overwrites top-level fields the patch actually touched. Iterates
 * `TICKET_UPDATE_FIELD_KEYS` — the same list `buildTicketUpdatePatches`
 * walks — so a new `UpdateTicketArgs` field can't silently rebase queued
 * writes on a stale snapshot by being copied in one place and not the
 * other.
 */
function applyPatchToTicket(
  ticket: LibraTicket,
  patch: Omit<UpdateTicketArgs, 'ticketId'>
): LibraTicket {
  const next = { ...ticket } as unknown as Record<string, unknown>
  for (const key of TICKET_UPDATE_FIELD_KEYS) {
    const value = patch[key]
    if (value === undefined) continue
    next[key] = value
  }
  return next as unknown as LibraTicket
}
